import React, { useEffect, useState } from 'react'
import fetch from 'isomorphic-fetch'

import { PartyBadge, Loader } from '@elements'
import { CandidateDetailCard, CandidateDetailHeader } from './CandidateInfoCard'
import { ReadMoreAccordion } from './ReadMoreAccordion/ReadMoreAccordion'
import { Icon } from './Icon/Icon'

import { getImgUrl } from '@utils'

class Api {
  constructor() {
    this.base_url = `${process.env.GATSBY_API_URL}`
  }

  apiRequest = endpoint => {
    return fetch(endpoint).then(res => res.json())
  }

  representativeInfoByName = name => {
    const endpoint = `${this.base_url}/wp-json/elections/v1/reps/${name}`
    return this.apiRequest(endpoint)
  }
}

const api = new Api()

export const CandidateDetail = props => {
  const [loading, setLoading] = useState()
  const [imageLoaded, setImageLoaded] = useState(false)
  const [results, setResults] = useState({})

  useEffect(() => {
    setLoading(true)
    api
      .representativeInfoByName(encodeURIComponent(props.candidateSlug))
      .then(data => {
        setResults(data)
        document.title = `${data.results.post.post_title} | Candidate Details | Indiana Citizen`
        setLoading(false)
      })
      .catch(e => {
        console.log(e)
      })
  }, [props.candidateSlug])

  const imgSrc = results.results && getImgUrl(results.results)

  // We're checking for image being loaded
  // If no image loaded yet, render the loader
  // Just keeps from janky image painting
  const objImg = new Image()
  objImg.src = imgSrc
  objImg.onload = () => {
    setImageLoaded(true)
  }

  return (
    <section className="py-20">
      <div className="container relative">
        {loading ? <Loader /> : null}
        {results.results && (
          <div className="flex flex-wrap">
            {/* Left Column Start */}
            <div className="w-full xl:w-2/3">
              {/* Candidate Main Card Start */}
              <div className="flex flex-wrap">
                <div className="relative flex justify-center items-center w-full overflow-hidden lg:flex lg:w-1/3 lg:pb-0 lg:h-auto">
                  {imageLoaded ? (
                    <img
                      src={imgSrc}
                      alt={results.results.post.post_title}
                      className="object-cover h-full w-full"
                    />
                  ) : (
                    <div className="relative w-full h-full bg-gray-200">
                      <Loader />
                    </div>
                  )}
                </div>
                {/* Candidate Main Info Start */}
                <figcaption className="relative flex flex-col items-start w-full mt-6 lg:mt-0 lg:w-2/3 lg:pl-10 xl:pl-12">
                  {/* Candidate Name */}
                  <div className="font-sans text-3xl font-bold uppercase">
                    {results.results.post.post_title}
                  </div>
                  {/* Candidate Position */}
                  <div className="mt-2 text-lg leading-tight text-gray-400">
                    {results.results.fields.vsapi_title}
                  </div>
                  {/* This needs fixed, it is just always true right now */}
                  {/* <ReelectionBadge
                    reelection={
                      results.results.fields &&
                      results.results.fields.vsapi_election_status
                    }
                  /> */}
                  {/* Party Badge */}
                  <div className="mt-8">
                    <PartyBadge
                      party={
                        results.results.fields.gapi_party !== ''
                          ? results.results.fields.gapi_party
                          : results.results.fields.vsapi_election_parties
                      }
                    />
                  </div>
                  {/* Party Badge End */}
                  {/* Misc. Personal Details Start */}
                  <div className="w-full max-w-xs mt-8">
                    {results.results.fields.vsapi_birth_date && (
                      <div className="flex justify-between">
                        <span className="inline-block mr-2 text-gray-300">
                          Birth Date:
                        </span>
                        {results.results.fields.vsapi_birth_date}
                      </div>
                    )}
                    {/* Religion */}
                    {results.results.fields.vsapi_religion && (
                      <div className="flex justify-between">
                        <span className="inline-block mr-2 text-gray-300">
                          Religion:
                        </span>
                        {results.results.fields.vsapi_religion}
                      </div>
                    )}
                    {/* Religion End */}
                  </div>
                  {/* Misc. Personal Details End */}
                </figcaption>
                {/* Candidate Main Info End */}
              </div>
              {/* Candidate Main Card End */}
              {results.results.fields.candidate_narrative && (
                <>
                  <div
                    className="hidden mt-16 overflow-hidden prose prose-xl xl:block"
                    dangerouslySetInnerHTML={{
                      __html: results.results.fields.candidate_narrative,
                    }}
                  ></div>
                  <ReadMoreAccordion
                    className="block xl:hidden"
                    accordionContent={
                      results.results.fields.candidate_narrative
                    }
                  />
                </>
              )}

              {/* Contributors Start */}
              {results.results.fields.key_votes && (
                <div className="max-w-3xl mt-16">
                  <CandidateDetailCard>
                    <div className="prose prose-lg">
                      <CandidateDetailHeader>Key Votes</CandidateDetailHeader>
                      <div
                        className="mt-8"
                        dangerouslySetInnerHTML={{
                          __html: results.results.fields.key_votes,
                        }}
                      ></div>
                    </div>
                  </CandidateDetailCard>
                </div>
              )}
              {/* Contributors End */}
            </div>
            {/* Left Column End */}

            {/* Right Column Start (White Cards) */}
            <div className="w-full mt-16 xl:w-1/3 xl:mt-0">
              {/* Contact Information Start */}
              <CandidateDetailCard>
                <CandidateDetailHeader>
                  Contact Information
                </CandidateDetailHeader>
                <ul className="mt-6 space-y-6">
                  {/* Phone Start */}
                  {results.results.fields.gapi_phones && (
                    <li className="flex items-start leading-snug">
                      <Icon icon="phone" />
                      <div className="space-y-1 font-bold text-left">
                        {results.results.fields.gapi_phones.map(
                          (phone, idx) => (
                            <a
                              key={idx}
                              className="block text-current underline"
                              href={`tel:${phone.phone}`}
                            >
                              {phone.phone}
                            </a>
                          )
                        )}
                      </div>
                    </li>
                  )}
                  {/* Phone End */}
                  {/* Address Start */}
                  {results.results.fields.gapi_address && (
                    <li className="flex items-start leading-snug">
                      <Icon icon="location" />
                      <div className="font-bold text-left ">
                        {results.results.fields.gapi_address.map(
                          (address, idx) => (
                            <div key={idx}>
                              <span className="block">
                                {address.address.line1}
                              </span>
                              <span className="block">
                                {address.address.line2}
                              </span>
                              <span className="block">
                                {address.address.city}, {address.address.state}{' '}
                                {address.address.zip}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    </li>
                  )}
                  {/* Address End */}
                  {/* Email Start */}
                  {results.results.fields.gapi_emails && (
                    <li className="flex items-start leading-snug">
                      <Icon icon="mail" />
                      <div className="max-w-full font-bold break-all">
                        {results.results.fields.gapi_emails.map(
                          (email, idx) => (
                            <a
                              className="block text-current underline"
                              href={`mailto:${email.email}`}
                              key={idx}
                            >
                              {email.email}
                            </a>
                          )
                        )}
                      </div>
                    </li>
                  )}
                  {/* Email End */}
                  {/* Url Start */}
                  {results.results.fields.gapi_urls && (
                    <li className="flex items-start w-full leading-snug">
                      <Icon icon="website" />
                      <div className="max-w-full space-y-1 font-bold break-all">
                        {results.results.fields.gapi_urls.map((url, idx) => (
                          <a
                            className="text-current underline"
                            href={`${url.url}`}
                            key={idx}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {url.url}
                          </a>
                        ))}
                      </div>
                    </li>
                  )}
                  {/* Url End */}
                </ul>
              </CandidateDetailCard>
              {/* Contact Information End */}

              {/* Career Start */}
              {results.results.fields.professional_history && (
                <CandidateDetailCard>
                  <div className="prose prose-lg">
                    <CandidateDetailHeader>
                      Professional Career
                    </CandidateDetailHeader>
                    <div
                      className="mt-8"
                      dangerouslySetInnerHTML={{
                        __html: results.results.fields.professional_history,
                      }}
                    ></div>
                  </div>
                </CandidateDetailCard>
              )}
              {/* Career End */}

              {/* Education Start */}
              {results.results.fields.vsapi_education && (
                <CandidateDetailCard>
                  <div className="prose prose-lg">
                    <CandidateDetailHeader>Education</CandidateDetailHeader>
                    <ul className="mt-8">
                      {results.results.fields.vsapi_education.map(
                        (school, idx) => (
                          <li key={idx} className="leading-normal">
                            {school.full_text}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                </CandidateDetailCard>
              )}
              {/* Education End */}

              {/* Committees Start */}
              {results.results.fields.committees && (
                <CandidateDetailCard>
                  <div className="prose prose-lg">
                    <CandidateDetailHeader>Committees</CandidateDetailHeader>
                    <div
                      className="mt-8"
                      dangerouslySetInnerHTML={{
                        __html: results.results.fields.committees,
                      }}
                    ></div>
                  </div>
                </CandidateDetailCard>
              )}
              {/* Committees End */}

              {/* Ratings Start */}
              {results.results.fields.vsapi_ratings && (
                <CandidateDetailCard>
                  <div className="prose prose-lg">
                    <CandidateDetailHeader>Ratings</CandidateDetailHeader>
                    <div className="mt-8 space-y-3">
                      {results.results.fields.vsapi_ratings.map(
                        (rating, idx) => (
                          <div
                            className="flex items-start justify-between leading-normal"
                            key={idx}
                          >
                            <span className="mr-8">
                              {rating.sig_name}
                              <span className="block text-sm text-gray-300">
                                {rating.name}
                              </span>
                            </span>
                            <span className="font-bold">{rating.rating}</span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </CandidateDetailCard>
              )}
              {/* Ratings End */}

              {/* Contributors Start */}
              {results.results.fields.top_contributors && (
                <CandidateDetailCard>
                  <div className="prose prose-lg">
                    <CandidateDetailHeader>
                      Top Contributors
                    </CandidateDetailHeader>
                    <div
                      className="mt-8"
                      dangerouslySetInnerHTML={{
                        __html: results.results.fields.top_contributors,
                      }}
                    ></div>
                  </div>
                </CandidateDetailCard>
              )}
              {/* Contributors End */}

              {/* District Start */}
              {results.results.fields.district_population_information && (
                <CandidateDetailCard>
                  <div className="prose prose-lg">
                    <CandidateDetailHeader>District</CandidateDetailHeader>
                    <div
                      className="mt-8"
                      dangerouslySetInnerHTML={{
                        __html:
                          results.results.fields
                            .district_population_information,
                      }}
                    ></div>
                    {results.results.fields.district_map.url && (
                      <img
                        src={results.results.fields.district_map.url}
                        alt={`${results.results.post.post_title} district map`}
                      />
                    )}
                  </div>
                </CandidateDetailCard>
              )}
              {/* District End */}
            </div>
            {/* Right Column end (White Cards) */}
          </div>
        )}
      </div>
    </section>
  )
}
